import { ShoppingCart, Award, AlertTriangle, GitCompareArrows } from "lucide-react";
import { cartPermalink } from "@/lib/quizConfig";
import { useIsEmbedded } from "@/lib/embed";

const specLine = (p) => {
  const a = p.attributes || {};
  const parts = [];
  if (a.weight_oz) parts.push(`${a.weight_oz} oz`);
  if (a.thickness_mm) parts.push(`${a.thickness_mm}mm`);
  if (a.core) parts.push(`${a.core} core`);
  return parts.join(" • ");
};

const primaryVariant = (p) => {
  const av = (p.variants || []).find((v) => v.available);
  return av || (p.variants || [])[0];
};

export const PaddleCard = ({
  paddle,
  isTop,
  compareActive,
  compareSelected,
  onToggleCompare,
  onAdd,
}) => {
  const variant = primaryVariant(paddle);
  const embedded = useIsEmbedded();
  const linkTarget = embedded ? "_top" : "_blank";
  const inv = paddle.inventory_total || 0;
  const soldOut = !paddle.available;
  const lowStock = !soldOut && inv > 0 && inv < 3;

  return (
    <article
      className={`cd-result cd-fade-in ${isTop ? "top-match" : ""}`}
      data-testid={`paddle-card-${paddle.product_id}`}
    >
      <div className="cd-result-badges">
        {isTop && (
          <span className="cd-chip cd-chip-primary" data-testid="badge-top-match">
            <Award size={12} /> Top match
          </span>
        )}
        {paddle.score !== undefined && (
          <span className="cd-chip cd-chip-accent">{paddle.score}% match</span>
        )}
        {soldOut && (
          <span className="cd-chip cd-chip-danger">
            <AlertTriangle size={12} /> Sold out
          </span>
        )}
        {lowStock && (
          <span className="cd-chip cd-chip-danger">
            Only {inv} left
          </span>
        )}
      </div>

      <div className="cd-result-image-wrap">
        {paddle.image ? (
          <img src={paddle.image} alt={paddle.title} loading="lazy" />
        ) : (
          <div className="cd-skel" style={{ width: "100%", height: "100%" }} />
        )}
      </div>

      <div className="cd-result-body">
        <h3 className="cd-result-title">{paddle.title}</h3>
        <div className="cd-result-spec">{specLine(paddle)}</div>
        {paddle.reason && (
          <div className="cd-result-reason">Why: {paddle.reason}.</div>
        )}

        <div className="cd-result-footer">
          <div>
            <div className="cd-money" style={{ fontSize: 18 }}>
              ${paddle.price?.toFixed(2)}
            </div>
            {paddle.vendor && (
              <div className="cd-micro">{paddle.vendor}</div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {compareActive && (
              <button
                className="cd-btn-secondary"
                onClick={() => onToggleCompare(paddle)}
                data-testid={`paddle-compare-toggle-${paddle.product_id}`}
                style={{
                  minWidth: 44,
                  padding: "10px 12px",
                  background: compareSelected ? "var(--cd-primary)" : "#fff",
                  color: compareSelected ? "#fff" : "var(--cd-primary)",
                }}
                aria-pressed={compareSelected}
                title="Compare"
              >
                <GitCompareArrows size={16} />
              </button>
            )}
            <button
              className="cd-btn-primary"
              disabled={soldOut || !variant}
              onClick={() => onAdd(paddle, variant)}
              data-testid={`paddle-add-btn-${paddle.product_id}`}
            >
              <ShoppingCart size={16} />
              {soldOut ? "Sold out" : "Add to cart"}
            </button>
          </div>
        </div>

        {variant && (paddle.options?.[0]?.values?.length || 0) > 1 && (
          <div className="cd-micro" style={{ marginTop: 4 }}>
            {paddle.options[0].name}: {variant.title}
          </div>
        )}

        {variant && (
          <a
            href={cartPermalink(variant.variant_id, 1)}
            target={linkTarget}
            rel="noreferrer"
            className="cd-micro"
            style={{ color: "var(--cd-primary)", textDecoration: "none", marginTop: 2 }}
            data-testid={`paddle-shopify-link-${paddle.product_id}`}
          >
            Buy on clubdinksupply.com →
          </a>
        )}
      </div>
    </article>
  );
};
